import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../../assets/api/endpoints';
import { OrderType } from '../../models/Orders';
import {
	Button,
	Form,
	Input,
	Select,
	Table,
	DatePicker,
	ConfigProvider,
	Grid,
	Collapse,
	TimeRangePickerProps,
} from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import { getUserInfo } from '../../helpers/localStorageHandler';
import { Row, Col } from 'antd';
import { getAppealTaskColumns } from '../../helpers/appealTaskColumn';
import { AdminList } from '../../models/AdminList';
import Header from '../../components/Header/Header';
import { SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Country } from '../../models/Country';
import { PaymentMethod } from '../../models/PaymentMethod';
import { useCountriesContext } from '../../store/CountriesContext';
import { themeConfig } from '../WalletTransactions/config';
import { TFunction } from 'i18next';
import { StyledPagination } from '../../components/table.style';
import { useConfigurationContext } from '../../store/configurationContext';
import { AppealTask } from '../../models/AppealTask';

const getUser = (list: AdminList[], userId: string, t: TFunction<'translation', undefined, 'translation'>) => {
	return list.find((item) => item.id === +userId)?.fullName ?? t('all');
};
const DATE_FORMAT = 'YYYY-MM-DD';
function AppealTasks() {
	const { countriesState } = useCountriesContext();
	const navigate = useNavigate();
	const { configurationState } = useConfigurationContext();
	const themeColor = configurationState.find((item) => item.configKey === 'theme_color')?.value || '';
	const secondary_color = configurationState.find((item) => item.configKey === 'secondary_color')?.value || '';
	const Company_Name = configurationState.find((item) => item.configKey === 'Company_Name')?.value || '';
	const hideUserSelect = useMemo(() => Company_Name.toLowerCase() === 'MaxifyFx'.toLowerCase(), [configurationState]);
	const { lg } = Grid.useBreakpoint();
	const [searchParams, setSearchParam] = useSearchParams();

	const [pagesize, setPageSize] = useState('10');
	const [orderStatusFilter, setOrderStatusFilter] = useState(searchParams.get('orderStatusFilter'));
	const { t } = useTranslation();
	const [orders, setOrders] = useState([]);
	const [adminList, setadminList] = useState<AdminList[]>([]);
	const [ordersLoading, setOrdersLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('searchTerm') || '');
	const [sortBy, setSortBy] = useState<string>('');
	const [totalElements, setTotalElements] = useState(0);
	const [page, setPage] = useState(0);
	const [offerTypeFilter, setOfferTypeFilter] = useState(searchParams.get('offerTypeFilter'));
	const userInfo = useMemo(() => getUserInfo(), []);
	const [admin, setadmin] = useState(`${hideUserSelect ? '' : searchParams.get('admin') || userInfo?.id || ''}`);
	const [searchValue, setSearchValue] = useState('');
	const [countries, setCountries] = useState<Country[]>([]);
	const [countryFilter, setCountryFilter] = useState<any>(searchParams.get('country'));
	const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
	const [activeKey, setActiveKey] = useState<string[] | string>(lg ? ['1'] : []);
	const [paymentMethodFilter, setPaymentMethodFilter] = useState(searchParams.get('payment-method'));
	const [dateRange, setDateRange] = useState<string[]>([]);
	const isDev = window.location.hostname.includes('devb');

	const { RangePicker } = DatePicker;
	const onDateRangeChange = useCallback((values: TimeRangePickerProps['value']) => {
		const range = (values || [])?.map((value) => dayjs(value).format(DATE_FORMAT));
		setDateRange(range);
	}, []);

	useEffect(() => {
		axios
			.get(`${API_ENDPOINTS.getAdminList}`)
			.then((res) => {
				setadminList(res.data);
			})
			.catch((err) => console.error(err));
	}, []);
	const params = useMemo(() => {
		let sortValue = 'createdAt-desc';
		if (sortBy.startsWith('createdAt') || sortBy.startsWith('resolved')) {
			sortValue = sortBy;
		} else if (sortBy) {
			sortValue = `order#${sortBy}`;
		}
		return [
			['page', page],
			['pageSize', pagesize],
			['sort', sortValue],
			['wildSearch', searchValue],
		]
			.filter(([, value]) => !!value)
			.reduce(
				(params, [key, value]) => ({
					...params,
					[key as string]: value,
				}),
				{},
			);
	}, [pagesize, page, sortBy, searchValue]);
	const searchParameter = useMemo(() => {
		setPage(0);
		let paymentMethod = paymentMethodFilter
			? paymentMethods.find((item: any) => +item.methodId === +paymentMethodFilter)?.methodName
			: '';
		return [
			['createdAt', dateRange],
			['order%23type%3A', offerTypeFilter],
			['resolved%3A', orderStatusFilter],
			['order%23paymentMethod%23methodName%3B', paymentMethod],
			['order%23paymentMethod%23country%23countryCode%3B', countryFilter],
			['assigneeId%23adminId%3A', admin],
		]
			.filter(([key, value]) => {
				if (key === 'createdAt') return Boolean(value?.length);
				return Boolean(value);
			})
			.map(([key, value]) =>
				key === 'createdAt'
					? `createdAt%3E${value?.[0]}%2CcreatedAt%3C${dayjs(value?.[1]).format('YYYY-MM-DD 23:59:00')}`
					: `${key}${value}`,
			)
			.join('%2C');
	}, [offerTypeFilter, orderStatusFilter, countryFilter, paymentMethodFilter, dateRange, admin]);
	useEffect(() => {
		setOrdersLoading(true);
		const filter = searchParameter ? `?search=${searchParameter}` : '';
		axios
			.get(`${API_ENDPOINTS.getAppealTask}${filter}`, { params })
			.then((result) => {
				setOrders(result.data.data);
				setTotalElements(result.data.totalElements);
				setOrdersLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setOrdersLoading(false);
			});
	}, [params, searchParameter]);

	const resetFilters = () => {
		setOrderStatusFilter('');
		setOfferTypeFilter('');
		setSearchTerm('');
		setSortBy('');
		setDateRange([]);
		setadmin(`${userInfo?.id || ''}`);
		setSearchParam('');
	};

	const getColor = (type: string) => {
		if (type === OrderType.buy) {
			return 'green';
		} else if (type === OrderType.sell) {
			return 'red';
		} else {
			return 'black';
		}
	};

	useEffect(() => {
		const countriesData = countriesState.countries?.map((el: any) => {
			return {
				name: el.countryName,
				code: el.countryCode,
			};
		});
		setPaymentMethods([
			{ ...countriesState.paymentMethods[0], methodName: t<string>('all'), methodId: 0 },
			...countriesState.paymentMethods,
		]);
		setCountries([{ name: t<string>('all'), code: '' }, ...countriesData]);
	}, [countriesState]);

	useEffect(() => {
		const params = new URLSearchParams(searchParams);
		orderStatusFilter ? params.set('orderStatusFilter', orderStatusFilter) : params.delete('orderStatusFilter');
		offerTypeFilter ? params.set('offerTypeFilter', offerTypeFilter) : params.delete('offerTypeFilter');
		paymentMethodFilter ? params.set('payment-method', paymentMethodFilter) : params.delete('payment-method');
		countryFilter ? params.set('country', countryFilter) : params.delete('country');
		orderStatusFilter ? params.set('admin', admin) : params.delete('admin');
		searchValue ? params.set('searchValue', searchValue) : params.delete('search');
		if (params.size === 0) return;
		setSearchParam(params);
	}, [orderStatusFilter, offerTypeFilter, searchValue, admin, countryFilter, paymentMethodFilter, paymentMethods]);
	useEffect(() => {
		const delaySearch = setTimeout(() => {
			setSearchValue(searchTerm);
		}, 1000);
		return () => clearTimeout(delaySearch);
	}, [searchTerm]);

	useEffect(() => {
		const country: any = countriesState.countries.find((item) => item.countryCode === countryFilter);
		const methods = country?.paymentMethods
			? country.paymentMethods
			: [{ methodName: t<string>('all'), methodId: '' }, ...countriesState.paymentMethods];
		setPaymentMethods(methods);
	}, [countryFilter, countries, countriesState.paymentMethods, t]);

	useEffect(() => {
		// Open panel by default if large screen (`lg`) is active, otherwise close
		if (lg) {
			setActiveKey(['1']);
		} else {
			setActiveKey([]);
		}
	}, [lg]);
	return (
		<div>
			<Header title={t<string>('appealTask')} description={t<string>('appealTaskSubtitle')} />
			<div className='min-h-[84vh] mx-auto px-5 py-5 bg-white'>
				<Collapse activeKey={activeKey} onChange={(key) => setActiveKey(key)}>
					<Collapse.Panel header={t('Filter Options')} key='1'>
						<div className='border-[#000d1d]/10 rounded-md shadow-md py-3 pb-0 px-4 border border-solid border-gray-200'>
							<Form layout='vertical'>
								<Row gutter={16}>
									<Col xs={24} md={12} lg={8}>
										<Form.Item label={t<string>('type')}>
											<Select
												placeholder={t('all')}
												value={offerTypeFilter}
												onChange={(e: string) => setOfferTypeFilter(e)}
											>
												{[
													{
														TypeName: t('all'),
														value: '',
													},
													{
														TypeName: isDev ? t<string>('buy') : t<string>('deposit'),
														value: 'BUY',
													},
													{
														TypeName: isDev ? t<string>('Sell') : t<string>('withdrawal'),
														value: 'SELL',
													},
												].map((item, index) => (
													<Select.Option key={index} value={item.value} style={{ color: getColor(item.TypeName) }}>
														{item.TypeName}
													</Select.Option>
												))}
											</Select>
										</Form.Item>
									</Col>
									<Col xs={24} md={12} lg={8}>
										<Form.Item label={t<string>('countries')}>
											<Select
												placeholder={t('all')}
												value={countryFilter}
												onChange={(e: string) => {
													setCountryFilter(e);
												}}
												showSearch
												filterOption={(inputValue, option: any) =>
													option?.children ? option.children[1].toLowerCase().includes(inputValue.toLowerCase()) : false
												}
											>
												{countries.map((item, index) => (
													<Select.Option key={index} value={item.code}>
														{item.name !== t('all') && (
															<img
																src={`https://flagsapi.com/${item.code}/shiny/64.png`}
																alt='country'
																className='country-flag'
															/>
														)}
														{item.name}
													</Select.Option>
												))}
											</Select>
										</Form.Item>
									</Col>
									<Col xs={24} md={12} lg={8}>
										<Form.Item label={t<string>('paymentMethods')}>
											<Select
												placeholder={t('all')}
												value={paymentMethodFilter}
												onChange={(e: string) => {
													setPaymentMethodFilter(e);
												}}
												showSearch
												filterOption={(inputValue, option: any) =>
													option?.children ? option.children.toLowerCase().includes(inputValue.toLowerCase()) : false
												}
											>
												{paymentMethods.map((item, index) => (
													<Select.Option key={index} value={item.methodId}>
														{item.methodName}
													</Select.Option>
												))}
											</Select>
										</Form.Item>
									</Col>
									<Col xs={24} md={12} lg={8}>
										<Form.Item label={t<string>('Resolved')}>
											<Select
												placeholder={t('all')}
												value={orderStatusFilter}
												onChange={(e: string) => setOrderStatusFilter(e)}
											>
												{[
													{
														TypeName: t('all'),
														value: '',
													},
													{
														TypeName: t<string>('yes'),
														value: 'true',
													},
													{
														TypeName: t<string>('no'),
														value: 'false',
													},
												].map((item, index) => (
													<Select.Option
														key={index}
														value={item.value}
														style={{
															color: item.TypeName === t<string>('yes') ? 'green' : item.TypeName === 'No' ? 'red' : '',
														}}
													>
														{item.TypeName}
													</Select.Option>
												))}
											</Select>
										</Form.Item>
									</Col>
									{hideUserSelect ? null : (
										<Col xs={24} md={12} lg={8}>
											<Form.Item label={t<string>('adminUser')}>
												<Select
													placeholder={t('all')}
													value={getUser(adminList, admin, t)}
													onChange={(e: string) => setadmin(e)}
												>
													<Select.Option value=''>{t<string>('all')}</Select.Option>
													{adminList.map((item, index) => (
														<Select.Option key={index} value={item.id}>
															{item.fullName}
														</Select.Option>
													))}
												</Select>
											</Form.Item>
										</Col>
									)}
									<Col xs={24} md={24} lg={8}>
										<Form.Item label={t<string>('dateRange')}>
											<RangePicker onChange={(values) => onDateRangeChange(values)} className='w-full' />
										</Form.Item>
									</Col>
								</Row>
							</Form>
						</div>
					</Collapse.Panel>
				</Collapse>
				<Row gutter={[16, 16]} align='middle' className='my-2'>
					<Col xs={24} lg={24} className='flex gap-2 items-center justify-end !ps-0'>
						<Button
							onClick={resetFilters}
							className='bg-[#EAECEF] text-[#1E2329] border-none shadow-none'
							htmlType='button'
							type='primary'
							icon={<SyncOutlined />}
						></Button>
						<Input
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className='xss:w-36  md:w-64 border-none h-9 bg-[#F5F5F5] search-input'
							placeholder={t<string>('searchOrder')}
							prefix={<SearchOutlined />}
						/>
					</Col>
				</Row>
				<ConfigProvider theme={themeConfig}>
					<StyledPagination bgColor={themeColor} color={secondary_color}>
						<Table
							columns={getAppealTaskColumns(t, sortBy, setSortBy, !lg)}
							dataSource={orders}
							loading={ordersLoading}
							pagination={{
								pageSize: Number(pagesize),
								total: totalElements,
								showSizeChanger: false,
								onChange: (pageIndex) => setPage(pageIndex - 1),
								current: page + 1,
							}}
							onRow={(record: AppealTask) => ({
								onClick: (event: any) => {
									navigate(`/order-life-cycle/?id=${record.orderNumber}`);
								},
							})}
							tableLayout='fixed'
						/>
					</StyledPagination>
				</ConfigProvider>
			</div>
		</div>
	);
}

export default AppealTasks;
